/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React, { type ReactNode } from 'react';
import { useHistory } from 'react-router';

import { ListeTable, ListeTableContainer } from '@mairistem/liste';

import type { TypeActe } from '@/types';
import type { Pacs } from '@/types/Pacs';
import { getActeListeColumns, TYPES_ACTES_IMPLEMENTES } from '@/constants';
import { baseUrlApp } from '@/routes';
import ActeListeTableRow from './ActeListeTableRow';

interface ActeListeTableProps {
  items?: Array<Partial<Pacs>>;
  count?: number;
  mode?: string;
  perPage?: number;
  loading?: boolean;
  dashboard?: boolean;
  typeActe?: string;
  assistant?: boolean;
  onLoad: any;
  onSort: any;
  onSelect?: (item: Partial<Pacs>) => void;
}

const ActeListeTable = (props: ActeListeTableProps) => {
  const {
    items,
    mode,
    count,
    perPage,
    loading,
    dashboard,
    typeActe,
    assistant,
    onLoad,
    onSort,
    onSelect: onSelectProp,
  } = props;

  const history = useHistory();

  const [selectedItemId, setSelectedItemId] = React.useState<number | null>(
    null,
  );

  const renderRow = (item: any, invalidate: () => void) => (
    <ActeListeTableRow
      item={item}
      mode={mode}
      invalidate={invalidate}
      dashboard={dashboard}
      typeActe={typeActe}
      selected={selectedItemId === item?.identifiant}
    />
  );

  /**
   * Avec `onSelect`, la liste sert à CHOISIR un acte (assistant, mention).
   * Sans, elle sert à en ouvrir un (accueil, statistiques) : on va sur sa
   * fiche, comme les listes par type (AD-12899). Décisions et PACS n'ont pas
   * de fiche dans cette application, le clic est donc sans effet sur eux.
   */
  const handleSelect = (selectedItem: any) => {
    if (onSelectProp) {
      setSelectedItemId(selectedItem?.identifiant);
      onSelectProp(selectedItem);
      return;
    }

    const typeActeItem = selectedItem?.type_acte as TypeActe | undefined;

    if (
      typeActeItem &&
      TYPES_ACTES_IMPLEMENTES.includes(typeActeItem) &&
      selectedItem?.identifiant != null
    ) {
      history.push(`${baseUrlApp}${typeActeItem}/${selectedItem.identifiant}`);
    }
  };

  const {
    dateLabel,
    dateWidth = 2,
    nomLabel,
    parentLabels,
  } = getActeListeColumns(typeActe);

  // Colonnes en enfants DIRECTS du Header : cf. MentionListeTable.
  const renderDashboardHeader = React.useMemo(
    () => (
      <ListeTable.Header>
        <ListeTable.Column
          name="nomClassement"
          style={{ maxWidth: '10em' }}
          width={7}
        >
          Nom
        </ListeTable.Column>
        <ListeTable.Column name="typeActe" width={2}>
          Type d&apos;acte
        </ListeTable.Column>
      </ListeTable.Header>
    ),
    [],
  );

  // La reconnaissance affiche à la fois le nom (l'enfant) et les 2 parents :
  // moins de place pour chaque colonne que quand une seule des deux s'affiche.
  const showsBothNomAndParents = !!nomLabel && !!parentLabels;

  const renderStatsHeader = React.useMemo(
    () => (
      <ListeTable.Header>
        <ListeTable.Column name="numero" sortable width={2}>
          N° acte
        </ListeTable.Column>
        <ListeTable.Column
          name="registre"
          width={showsBothNomAndParents ? 3 : 4}
        >
          Registre
        </ListeTable.Column>
        {/* `dresse` : la clé de tri de l'API pour la date de l'acte. */}
        <ListeTable.Column name="dresse" sortable width={dateWidth}>
          {dateLabel}
        </ListeTable.Column>
        {nomLabel && (
          <ListeTable.Column
            name="nomClassement"
            width={showsBothNomAndParents ? 3 : 6}
          >
            {nomLabel}
          </ListeTable.Column>
        )}
        {parentLabels && (
          <ListeTable.Column name="partenaire1" width={3}>
            {parentLabels[0]}
          </ListeTable.Column>
        )}
        {parentLabels && (
          <ListeTable.Column name="partenaire2" width={3}>
            {parentLabels[1]}
          </ListeTable.Column>
        )}
      </ListeTable.Header>
    ),
    [dateLabel, dateWidth, nomLabel, parentLabels, showsBothNomAndParents],
  );

  const listItems = React.useMemo(() => {
    const list = items || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalized = (list as any[]).map((item) => {
      if (item?.identifiant == null && item?.id != null) {
        return { ...item, identifiant: item.id };
      }
      return item;
    });
    if (assistant) {
      // @ts-ignore
      return [
        { isAssistantEmptyRow: true, identifiant: 'empty-row' },
        ...normalized,
      ];
    }
    return normalized;
  }, [items, assistant]);

  return (
    <ListeTableContainer
      // @ts-ignore
      items={listItems}
    >
      <ListeTable
        items={listItems}
        loading={loading}
        perPage={perPage}
        count={count}
        onLoad={onLoad}
        onSort={onSort}
        onSelect={handleSelect}
      >
        {dashboard ? renderDashboardHeader : renderStatsHeader}
        {renderRow as unknown as ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default ActeListeTable;
